import * as React from 'react';
import { HcpGroup } from '@ixicare/resources';
import type { HcpGroupEntryProps } from '@ixicare/resources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Select, Button } from '@ixicare/ui';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useSite } from '~/hooks/site';
import { useZones } from '~/hooks/zone';
import { useHcpGroups } from '~/hooks/useHcpGroups';
import RemovableChip from '~components/RemovableChip';

// Extended ZoneSummary with plane color information
interface ZoneSummaryWithColor {
  id: string;
  name: string;
  planeUuid?: string;
  planeColor?: string;
}

// Pastel colors for different planes
const PLANE_COLORS = [
  '#fff3e0', // orange
  '#e3f2fd', // blue
  '#f3e5f5', // purple
  '#e8f5e9', // green
  '#fce4ec', // pink
  '#fff8e1', // amber
  '#e0f7fa', // cyan
  '#f1f8e9', // light green
];

export const ZoneAssignmentTab: React.FC = observer(() => {
  const queryClient = useQueryClient();
  const { notifications, sites } = useStores();
  const siteUuid = sites.selectedSite?.uuid;
  const [selectedPlane, setSelectedPlane] = React.useState<string>('');

  // Clear plane filter when site changes — plane uuids are not portable across sites.
  React.useEffect(() => {
    setSelectedPlane('');
  }, [siteUuid]);

  // Fetch site to get planes
  const site = useSite(siteUuid);
  const planeOptions = React.useMemo(
    () =>
      site?.planes?.map((p, index) => ({
        value: p.uuid,
        label: p.name,
        color: PLANE_COLORS[index % PLANE_COLORS.length],
      })) || [],
    [site]
  );

  // Create a map of planeUuid -> color for quick lookup
  const planeColorMap = React.useMemo(() => {
    const map = new Map<string, string>();
    planeOptions.forEach((p) => map.set(p.value, p.color));
    return map;
  }, [planeOptions]);

  // Fetch all groups
  const { data: groups } = useHcpGroups(siteUuid);

  // Fetch zones, optionally filtered by plane
  const allZonesData = useZones({
    siteUuid,
    planeUuid: selectedPlane || undefined,
  });

  // Transform ZoneEntry[] to ZoneSummaryWithColor[]
  const allZones = React.useMemo<ZoneSummaryWithColor[]>(() => {
    return allZonesData.map((zone) => ({
      id: zone.uuid,
      name: zone.name,
      planeUuid: zone.plane_uuid,
      planeColor: planeColorMap.get(zone.plane_uuid) || PLANE_COLORS[0],
    }));
  }, [allZonesData, planeColorMap]);

  // Mutation to assign zone to group
  const assignMutation = useMutation({
    mutationFn: ({ groupId, zoneId }: { groupId: string; zoneId: string }) =>
      HcpGroup.assignZoneToHcpGroup(groupId, zoneId),
    onSuccess: (updatedGroup) => {
      // Update the cache with the returned group data
      queryClient.setQueryData<HcpGroupEntryProps[]>(['hcpGroups', siteUuid], (old) => {
        if (!old) return old;
        return old.map((group) => (group.id === updatedGroup.id ? updatedGroup : group));
      });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.zone.assign.success'),
      });
    },
  });

  // Mutation to remove zone from group
  const removeMutation = useMutation({
    mutationFn: ({ groupId, zoneId }: { groupId: string; zoneId: string }) =>
      HcpGroup.unassignZoneFromHcpGroup(groupId, zoneId),
    onSuccess: (updatedGroup) => {
      // Update the cache with the returned group data
      queryClient.setQueryData<HcpGroupEntryProps[]>(['hcpGroups', siteUuid], (old) => {
        if (!old) return old;
        return old.map((group) => (group.id === updatedGroup.id ? updatedGroup : group));
      });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.zone.remove.success'),
      });
    },
    onError: () => {
      notifications.addUserActionNotification({
        notificationType: 'error',
        message: t('hcpGroups.zone.remove.error'),
      });
    },
  });

  return (
    <div>
      {planeOptions.length > 0 && (
        <div style={{ marginBottom: '20px', maxWidth: '300px' }}>
          <Select
            label={t('hcpGroups.filter.byFloor')}
            value={selectedPlane}
            onChange={setSelectedPlane}
            as="filter"
            options={[{ value: '', label: t('hcpGroups.filter.allFloor') }, ...planeOptions]}
            placeholder={t('hcpGroups.filter.allFloor')}
          />
        </div>
      )}
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.assigned.zones')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.add.zone')}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Array.isArray(groups) &&
            groups.map((group) => (
              <Table.Row key={group.id}>
                <Table.Col>{group.hcpGroupName}</Table.Col>
                <Table.Col>
                  <GroupZones
                    assignedZones={group.zones || []}
                    onRemove={(zoneId) => removeMutation.mutate({ groupId: group.id, zoneId })}
                    allZones={allZones}
                  />
                </Table.Col>
                <Table.Col>
                  <ZoneSelectorWithAddAll
                    allZones={allZones}
                    assignedZones={group.zones || []}
                    onAssign={(zoneId) => assignMutation.mutate({ groupId: group.id, zoneId })}
                    onAssignAll={(zoneIds) => {
                      zoneIds.forEach((zoneId) => assignMutation.mutate({ groupId: group.id, zoneId }));
                    }}
                  />
                </Table.Col>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
    </div>
  );
});

// Display assigned zones with remove buttons
function GroupZones({
  assignedZones,
  onRemove,
  allZones,
}: {
  assignedZones: ZoneSummaryWithColor[];
  onRemove: (zoneId: string) => void;
  allZones: ZoneSummaryWithColor[];
}) {
  // Filter assigned zones to only show those in allZones (filtered by plane)
  // and enrich with color from allZones
  const filteredZones = React.useMemo(() => {
    const allZonesMap = new Map(allZones.map((z) => [z.id, z]));
    return assignedZones
      .filter((zone) => allZonesMap.has(zone.id))
      .map((zone) => ({ ...zone, planeColor: allZonesMap.get(zone.id)?.planeColor }));
  }, [assignedZones, allZones]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      {filteredZones.length === 0 ? (
        <span style={{ color: '#999' }}>{t('hcpGroups.no.zones')}</span>
      ) : (
        filteredZones.map((zone) => (
          <RemovableChip
            key={zone.id}
            color={zone.planeColor || PLANE_COLORS[0]}
            onRemove={() => onRemove(zone.id)}
            removeTitle={t('hcpGroups.zone.remove.label')}
          >
            {zone.name}
          </RemovableChip>
        ))
      )}
    </div>
  );
}

// Zone selector dropdown with Add All button
function ZoneSelectorWithAddAll({
  allZones,
  assignedZones,
  onAssign,
  onAssignAll,
}: {
  allZones: ZoneSummaryWithColor[];
  assignedZones: ZoneSummaryWithColor[];
  onAssign: (zoneId: string) => void;
  onAssignAll: (zoneIds: string[]) => void;
}) {
  const [selectedZone, setSelectedZone] = React.useState<string>('');

  // Get zones not yet assigned to this group (shared logic)
  const assignedZoneIds = new Set(assignedZones.map((z) => z.id));
  const availableZones = allZones.filter((zone) => !assignedZoneIds.has(zone.id));

  const handleChange = (value: string) => {
    if (value) {
      onAssign(value);
      setSelectedZone('');
    }
  };

  const handleAddAll = () => {
    const zoneIds = availableZones.map((zone) => zone.id);
    if (zoneIds.length > 0) {
      onAssignAll(zoneIds);
    }
  };

  const options = [
    { value: '', label: t('hcpGroups.select.zone') },
    ...availableZones.map((zone) => ({ value: zone.id, label: zone.name })),
  ];

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <Select
          value={selectedZone}
          onChange={handleChange}
          options={options}
          placeholder={t('hcpGroups.select.zone')}
        />
      </div>
      <Button
        onClick={handleAddAll}
        lucide={Plus}
        disabled={availableZones.length === 0}
        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('hcpGroups.addAll.button')}
      </Button>
    </div>
  );
}
